import { useCallback, useMemo, useState } from 'react';

import type { SiteTimeline } from '@/domain/fishGroups';

import { lineageBlocksFor } from '../helpers/boardModel';
import { rowsHiddenBy } from '../helpers/lineageVisibility';
import type { LastClosure, LineageBlock, WeekAxis } from '../types';

export interface BoardModel {
    axis: WeekAxis;
    blocks: LineageBlock[];
    todayPct: number | null;
    /** For the banner's "Last harvest" or "Last closure". */
    lastClosure: LastClosure | null;
}

/**
 * The drawn board for a timeline, measured against the response's own clock; with
 * `combineChains`, a pen's chain of continuing groups is one row.
 */
export function useBoardModel(
    timeline: SiteTimeline | null,
    combineChains = false
): BoardModel | null {
    return useMemo(
        () => (timeline ? lineageBlocksFor(timeline, combineChains) : null),
        [timeline, combineChains]
    );
}

/** What an Origin press did, for the screen-reader announcement. */
export type OriginOutcome = 'shown' | 'hidden' | 'highlighted' | 'cleared';

/**
 * The last change to the board's lineage, for its live region: an Origin press, or a highlight
 * that another highlight or a checkbox cleared. `previous` names the row whose highlight a
 * `switched` press moved away from.
 */
export interface LineageNotice {
    kind: OriginOutcome | 'switched';
    name: string;
    previous: string | null;
}

/**
 * Which lineage blocks show their sources. "Show previous groups" sets every block, and an
 * Origin toggle flips one block against that; changing the global box clears the flips.
 * In a block that shows its sources, an Origin press hides only the earlier rows no other
 * shown row descends from (`lineageVisibility`); when it could hide none, it highlights the
 * row's lineage instead, and a second press clears either.
 */
export interface LineageExpansion {
    showPrevious: boolean;
    setShowPrevious: (value: boolean) => void;
    expandedBlocks: ReadonlySet<string>;
    toggleBlock: (blockKey: string) => void;
    collapsedRows: ReadonlySet<number>;
    highlightedRow: number | null;
    toggleOrigin: (blockKey: string, rowKey: number) => OriginOutcome | null;
    /** What to announce; null until the lineage first changes. */
    notice: LineageNotice | null;
}

export function useLineageExpansion(blocks: LineageBlock[]): LineageExpansion {
    const [showPrevious, setShowPreviousState] = useState(true);
    const [flipped, setFlipped] = useState<ReadonlySet<string>>(new Set());
    const [collapsedRows, setCollapsedRows] = useState<ReadonlySet<number>>(new Set());
    // The lit row's name is kept with it: after the scope changes the row is no longer on the
    // board, and a clear must still say which group it was.
    const [highlight, setHighlight] = useState<{ row: number; name: string } | null>(null);
    const highlightedRow = highlight?.row ?? null;
    const [notice, setNotice] = useState<LineageNotice | null>(null);
    const nameOf = (rowKey: number) =>
        blocks.flatMap((block) => block.rows).find((row) => row.groupId === rowKey)?.name ?? '';

    const expandedBlocks = useMemo(
        () =>
            new Set(
                blocks
                    .filter((block) => block.hasSources && showPrevious !== flipped.has(block.key))
                    .map((block) => block.key)
            ),
        [blocks, showPrevious, flipped]
    );

    // A checkbox change clears the highlight too; a screen reader is told, as for a press.
    const setShowPrevious = (value: boolean) => {
        setShowPreviousState(value);
        setFlipped(new Set());
        setCollapsedRows(new Set());
        if (highlight) setNotice({ kind: 'cleared', name: highlight.name, previous: null });
        setHighlight(null);
    };

    const toggleBlock = useCallback((key: string) => {
        setFlipped((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const press = (blockKey: string, rowKey: number): OriginOutcome | null => {
        const block = blocks.find((candidate) => candidate.key === blockKey);
        if (!block) return null;
        // A block without source rows (rows that feed each other both ways) is always open, as
        // TimelineBoard draws it; only a block the global box closed opens on a press.
        if (block.hasSources && !expandedBlocks.has(blockKey)) {
            toggleBlock(blockKey);
            return 'shown';
        }
        if (collapsedRows.has(rowKey)) {
            setCollapsedRows(new Set([...collapsedRows].filter((key) => key !== rowKey)));
            return 'shown';
        }
        if (highlightedRow === rowKey) {
            setHighlight(null);
            return 'cleared';
        }
        const newlyHidden = rowsHiddenBy(block.parentRows, collapsedRows, rowKey, block.liveRows);
        if (newlyHidden.length > 0) {
            setCollapsedRows(new Set([...collapsedRows, rowKey]));
            // The lit row's Origin is gone with it, so its highlight would have no way off.
            if (highlightedRow !== null && newlyHidden.includes(highlightedRow)) {
                setHighlight(null);
            }
            return 'hidden';
        }
        setHighlight({ row: rowKey, name: nameOf(rowKey) });
        return 'highlighted';
    };

    // Null when there is nothing to press any more (a refetch dropped the block).
    const toggleOrigin = (blockKey: string, rowKey: number): OriginOutcome | null => {
        const outcome = press(blockKey, rowKey);
        if (!outcome) return null;
        // Lighting one row clears another's highlight: both are said in one announcement.
        const switched =
            outcome === 'highlighted' && highlightedRow !== null && highlightedRow !== rowKey;
        setNotice({
            kind: switched ? 'switched' : outcome,
            name: nameOf(rowKey),
            previous: switched && highlight ? highlight.name : null,
        });
        return outcome;
    };

    return {
        showPrevious,
        setShowPrevious,
        expandedBlocks,
        toggleBlock,
        collapsedRows,
        highlightedRow,
        toggleOrigin,
        notice,
    };
}
